import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-scroll';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight';
import { StarIcon } from '@heroicons/react/20/solid';

import { Modal } from '../../../helpers/modal';
import { t } from '../../../helpers/t';

export function ListingRating({ section, small }) {
  const { reviews, average_ratings } = useSelector((state) => state.listing);

  return reviews && reviews.length > 0 && (
    <div className={classNames('flex text-stone-700', section ? 'text-xl items-start' : `text-${(small ? 'sm' : 'base')} items-center`)}>
      <StarIcon width={section ? 24 : (small ? 12 : 16)} />
      <div className={classNames('text-stone-900 flex items-center', section ? 'ml-1.5 font-medium' : 'ml-1 font-normal')}>
        {average_ratings.avg_rating}
      </div>
      {
        !small && (
          <>
            <div className="inline-block font-medium pt-1 leading-3 mx-1.5 text-stone-900 self-center">
              &middot;
            </div>
            {
              section
                ? (
                  <div className="text-stone-900 font-medium">
                    {reviews.length} {reviews.length > 1 ? 'reviews' : 'review'}
                  </div>
                )
                : (
                  <Link offset={-160} className="underline text-stone-900 underline-offset-2 decoration-1 font-normal cursor-pointer" to="reviews">
                    {reviews.length} {reviews.length > 1 ? 'reviews' : 'review'}
                  </Link>
                )
            }
          </>
        )
      }
    </div>
  );
}

const ListingReviewCard = ({ review }) => (
  <div className={
    classNames(
      'pl-4 border flex flex-col items-start justify-between rounded p-4 hover:bg-stone-100 border-b-2 border-l-4 border-stone-100 border-solid hover:border-stone-200 bg-stone-50'
    )
  }>
    <div className="relative flex items-center gap-x-3 mb-2">
      <div
        className="h-10 w-10 rounded-full bg-stone-300 flex items-center justify-center font-medium text-lg">
        {review.reviewer_name[0].toUpperCase()}
      </div>
      <div className="leading-6 flex flex-col">
        <div className="text-md font-medium text-stone-900">
          {review.reviewer_name}
        </div>
        <div className="flex items-center gap-x-4 text-xs text-stone-900 font-light">
          {moment.utc(review.created_at).format('MMM D, YYYY')}
        </div>
      </div>
    </div>

    <div className="flex items-center my-2">
      <div className="relative">
        <StarRating rating={review.avg_rating} />
      </div>
    </div>

    {
      review.body
        ? (
          <div className="group relative text-base font-extralight text-stone-900">
            {review.body}
          </div>
        )
        : (
          <div className="group relative text-base font-extralight text-stone-500 italic">
            {t('listings.show.sections.reviews.noComment')}
          </div>
        )
    }
  </div>
);

const StarRating = ({ rating }) => (
  <div className="relative z-0">
    <div className="flex items-center">
      <StarIcon width={16} className="text-stone-200 flex-shrink-0" />
      <StarIcon width={16} className="text-stone-200 flex-shrink-0" />
      <StarIcon width={16} className="text-stone-200 flex-shrink-0" />
      <StarIcon width={16} className="text-stone-200 flex-shrink-0" />
      <StarIcon width={16} className="text-stone-200 flex-shrink-0" />
    </div>
    <div className={`absolute top-0 left-0 z-10 overflow-hidden w-[${rating / 5 * 100}%]`}>
      <div className="flex items-center">
        <StarIcon width={16} className="text-stone-700 flex-shrink-0" />
        <StarIcon width={16} className="text-stone-700 flex-shrink-0" />
        <StarIcon width={16} className="text-stone-700 flex-shrink-0" />
        <StarIcon width={16} className="text-stone-700 flex-shrink-0" />
        <StarIcon width={16} className="text-stone-700 flex-shrink-0" />
      </div>
    </div>
  </div>
);

const reviewsOrderFns = [
  [
    (review) => (moment(review.created_at).unix())
  ],
  ['desc']
];

export function ListingReviews() {
  let [isModalOpen, setIsModalOpen] = useState(false);
  const { reviews } = useSelector((state) => state.listing);

  const allReviews =  orderBy(reviews, ...reviewsOrderFns);
  const reviewsWithComment = allReviews.filter((review) => !!review.body);
  const reviewsWithoutComment = allReviews.filter((review) => !review.body);

  let nDisplayedReviews = reviews.length > 3 ? 3 : reviews.length;

  return reviews.length
    ? (
      <div id="reviews" className="px-4 md:px-0 py-6">
        <h2 className="text-stone-900 text-2xl font-semibold mb-4" style={{ marginBottom: '16px !important' }}>
          {t('listings.show.sections.reviews.header')}
        </h2>
        <div className="flex items-center mb-4">
          <ListingRating section />
        </div>

        <div className="mt-4">
          {reviewsWithComment.map((review) => {
            return (nDisplayedReviews-- > 0) && (
              <div className="mb-2" key={`listing-review-card-${review.id}`}>
                <ListingReviewCard review={review}/>
              </div>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          {reviewsWithoutComment.map((review, index) => {
            return (nDisplayedReviews-- > 0) && (
              <div key={`listing-review-card-${review.id}`} className={(index % 2) === 0 && nDisplayedReviews === 0 ? "col-span-2" : "col-span-1"}>
                <ListingReviewCard review={review} />
              </div>
            );
          })}
        </div>

        {
          (reviews.length > 3) && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center py-3 px-4 bg-stone-100 hover:bg-stone-200 rounded-lg transition-all duration-200 text-stone-700 hover:text-stone-900 font-medium mt-4"
              style={{border: '1px solid #d6d3d1', borderRadius: '8px'}}
            >
              <span>{t('listings.show.sections.reviews.seeAll')}</span>
              <FontAwesomeIcon icon={faAngleRight} className="ml-2" aria-hidden />
            </button>
          )
        }

        <Modal
          id="listing-reviews-modal"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          header={(
            <h2 id="reviews" className="m-0 text-stone-900 text-xl font-medium px-4 md:px-0">
              {t('listings.show.sections.reviews.all')}
            </h2>
          )}
        >
          <div className="p-4">
            <div className="flex items-center">
              <ListingRating section />
            </div>
            <div className="mt-4">
              {allReviews.map((review) => {
                return (
                  <div className="mb-2" key={`listing-all-reviews-card-${review.id}`}>
                    <ListingReviewCard review={review} />
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      </div>
    )
    : (
      <div id="reviews" className="px-4 md:px-0 py-6">
        <h2 className="text-stone-900 text-2xl font-semibold mb-4" style={{ marginBottom: '16px !important' }}>
          {t('listings.show.sections.reviews.header')}
        </h2>

        <div className="mt-2 relative block w-full rounded-lg text-center py-4 hover:border-gray-200 text-md font-extralight text-stone-600 border-0 hover:bg-stone-100 border-b border-l-2 border-stone-100 border-solid hover:border-stone-200 bg-stone-50">
          {t('listings.show.sections.reviews.emptyBody')}
        </div>
      </div>
    )
}
